import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { Seo } from '../components/Seo';
import { SITE, breadcrumbJsonLd } from '../data/seoData';
import { SALES_RESOURCE_CATEGORIES } from '../data/salesResourcesData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const altFor = (item, cat) =>
  `${item.title} — ${cat.name} ${item.type === 'video' ? 'video tutorial' : 'PDF download preview'} from Evergreen Graphics Group`;

export default function SalesResourceCategory() {
  const { categorySlug } = useParams();
  const cat = SALES_RESOURCE_CATEGORIES.find((c) => c.slug === categorySlug);
  if (!cat) return <Navigate to="/sales-resources" replace />;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${cat.name} — Marketing and Sales Resources`,
      url: `${SITE.url}/sales-resources/${cat.slug}`,
      description: cat.description,
      hasPart: cat.items.map((item) =>
        item.type === 'video'
          ? {
              '@type': 'VideoObject',
              name: item.title,
              description: `${item.title} — application tutorial by ${SITE.name}.`,
              contentUrl: `${SITE.url}${item.file}`,
              uploadDate: '2026-08-28',
              thumbnailUrl: item.thumb ? `${SITE.url}${item.thumb}` : `${SITE.url}/evergreen-logo.png`,
            }
          : {
              '@type': 'ImageObject',
              name: item.title,
              contentUrl: `${SITE.url}${item.thumb}`,
              description: altFor(item, cat),
            }
      ),
    },
    breadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: 'Sales Resources', path: '/sales-resources' },
      { name: cat.name, path: `/sales-resources/${cat.slug}` },
    ]),
  ];

  const firstThumb = cat.items.find((i) => i.thumb)?.thumb;

  return (
    <div className="min-h-screen bg-white" data-testid={`sales-resources-${cat.slug}`}>
      <Seo
        title={`${cat.name} — Free Downloads | Evergreen Graphics Group`}
        description={`${cat.description} Free downloads from Evergreen Graphics Group — custom stickers, labels, decals & transfers.`}
        path={`/sales-resources/${cat.slug}`}
        image={firstThumb ? `${SITE.url}${firstThumb}` : undefined}
        jsonLd={jsonLd}
      />

      <section className="bg-[#F4F7F2]">
        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
          <nav className="flex items-center gap-1.5 text-sm text-[#1F3D2C]/60 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#4A7C59]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/sales-resources" className="hover:text-[#4A7C59]">Sales Resources</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#14281D] font-medium">{cat.name}</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#14281D]">{cat.name}</h1>
          <p className="mt-3 text-base md:text-lg text-[#1F3D2C]/80 max-w-2xl">{cat.description}</p>
          <p className="mt-2 text-sm text-[#4A7C59]">
            {cat.items[0]?.type === 'video'
              ? 'Click play to watch any tutorial.'
              : 'Hover to zoom in for a closeup — click to open the PDF in a new tab with download available.'}
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {cat.items.map((item) =>
            item.type === 'video' ? (
              <div
                key={item.slug}
                className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden shadow-sm"
                data-testid={`resource-video-${item.slug}`}
              >
                <video
                  controls
                  preload="metadata"
                  poster={item.thumb || undefined}
                  className="w-full aspect-video bg-black"
                  src={`${API_URL}${item.file}`}
                  title={altFor(item, cat)}
                />
                <div className="p-4">
                  <h2 className="text-sm md:text-base font-semibold text-[#14281D] leading-snug">{item.title}</h2>
                </div>
              </div>
            ) : (
              <button
                key={item.slug}
                type="button"
                onClick={() => window.open(`${API_URL}${item.file}`, '_blank', 'noopener')}
                className="group text-left bg-white rounded-2xl border border-[#E5E7EB] hover:border-[#6FBF73] hover:shadow-2xl transition-all overflow-hidden cursor-zoom-in"
                data-testid={`resource-pdf-${item.slug}`}
                title={`Open ${item.title} (PDF) in a new tab`}
              >
                <div className="relative h-72 overflow-hidden bg-[#F4F7F2]">
                  <img
                    src={item.thumb}
                    alt={altFor(item, cat)}
                    loading="lazy"
                    className="w-full h-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-125"
                  />
                  <div className="absolute inset-0 bg-[#14281D]/0 group-hover:bg-[#14281D]/25 transition-colors duration-300 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/95 text-[#14281D] text-xs font-semibold px-4 py-2 rounded-full shadow-lg flex items-center gap-1.5">
                      <ExternalLink className="w-3.5 h-3.5" /> Open PDF
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h2 className="text-sm md:text-base font-semibold text-[#14281D] leading-snug group-hover:text-[#2F5D3A] transition-colors">
                    {item.title}
                  </h2>
                </div>
              </button>
            )
          )}
        </div>
      </section>

      <footer className="bg-[#14281D] text-white py-10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-100">© 2026 {SITE.name}. All rights reserved.</p>
          <p className="text-sm text-emerald-200/80 mt-2">
            {SITE.address.street}, {SITE.address.city}, {SITE.address.region} {SITE.address.postal}
          </p>
          <p className="text-sm text-emerald-200/80 mt-1">Phone: {SITE.phone} | Email: {SITE.email}</p>
        </div>
      </footer>
    </div>
  );
}
